"use client";

import type { ReactNode } from "react";
import type { AgentChanges, EntryChange, TextChange } from "@/generated";
import { ModelSelectCompact } from "@/components/SpotflowV2/CustomNodes/BaseNode/ModelSelectCompact";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { cn } from "@/lib/utils";
import { isRecord } from "@/utils/isRecord";
import { extractErrorMessage } from "@/utils/plugins";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import type { ToolPartBase } from "../ToolComponents";
import {
  getApprovalDecision,
  isActionablePendingApproval,
} from "./approvalGate";
import { formatValue, humanize } from "./approvalValues";
import type { ApprovalView, EntityKind, StepOp } from "./approvalView";
import {
  EntityApprovalBody,
  EntityPill,
  FieldList,
  HeadEntity,
  InlineRow,
  LabeledRow,
  OpBadge,
} from "./EntityApprovalBody";

// Discriminated on ``kind``, which the response marks as always present.
type AgentChange = AgentChanges["changes"][number];

interface CapabilityMove {
  name: string;
  title: string;
  from: number;
  to: number;
}

const ENTRY_OP: Record<EntryChange["op"], StepOp> = {
  added: "add",
  removed: "remove",
  changed: "edit",
};

// Entries that point at a page the card can link to.
const ENTRY_KIND: Partial<
  Record<NonNullable<EntryChange["entity"]>, EntityKind>
> = {
  knowledge_base: "knowledge_base",
  table: "table",
  workflow: "workflow",
  agent: "agent",
};

const valueText = (v: unknown) => formatValue(v) ?? "Not set";

const versionText = (v: number) => (v === 0 ? "Off" : `v${v}`);

const errorDetail = (error: unknown) =>
  extractErrorMessage(error, "Could not read what this change does.");

function outputRecord(part: ToolPartBase): Record<string, unknown> | null {
  return part.state === "output-available" && isRecord(part.output)
    ? part.output
    : null;
}

// What the call changed, once it ran: agents_update returns it, so the card
// keeps showing it after the agent has moved on.
function ranChanges(part: ToolPartBase): AgentChange[] | null {
  const changes = outputRecord(part)?.changes;
  return Array.isArray(changes) ? (changes as AgentChange[]) : null;
}

function EntryRow({ entry }: { entry: EntryChange }) {
  const kind = entry.entity ? ENTRY_KIND[entry.entity] : undefined;
  const struck = entry.op === "removed";
  return (
    <div className="space-y-1">
      <div className="flex min-w-0 items-center gap-2">
        <OpBadge op={ENTRY_OP[entry.op]} />
        {kind && entry.id ? (
          <EntityPill
            entity={{ kind, id: entry.id, name: entry.label }}
            struck={struck}
          />
        ) : (
          <span
            className={cn(
              "min-w-0 truncate text-foreground",
              struck && "line-through decoration-red-400/70"
            )}
          >
            {entry.label}
          </span>
        )}
      </div>
      {(entry.fields ?? []).map((field) => (
        <div key={field.field} className="pl-6">
          <InlineRow
            // Cards saved before fields carried their editor label have none.
            label={field.label || humanize(field.field)}
            edit
            from={valueText(field.before)}
          >
            {valueText(field.after)}
          </InlineRow>
        </div>
      ))}
    </div>
  );
}

function TextLines({ change }: { change: TextChange }) {
  return (
    <div className="rounded-md bg-muted px-2 py-1.5 font-mono text-[11px] leading-relaxed">
      {change.lines.map((line, i) =>
        line.op === "gap" ? (
          <div key={i} className="text-muted-foreground">
            …
          </div>
        ) : (
          <div
            key={i}
            className={cn(
              "whitespace-pre-wrap break-words",
              line.op === "same" && "text-muted-foreground",
              line.op === "removed" && "text-red-500 line-through",
              line.op === "added" && "text-emerald-600"
            )}
          >
            {line.op === "removed" ? "− " : line.op === "added" ? "+ " : "  "}
            {line.text}
          </div>
        )
      )}
    </div>
  );
}

function ChangeRow({ change }: { change: AgentChange }) {
  if (change.kind === "text") {
    return (
      <LabeledRow label={change.label}>
        <TextLines change={change} />
      </LabeledRow>
    );
  }
  if (change.kind === "list") {
    return (
      <LabeledRow label={change.label}>
        <div className="space-y-1.5">
          {change.entries.map((entry) => (
            <EntryRow
              key={`${entry.op}:${entry.id ?? entry.label}`}
              entry={entry}
            />
          ))}
        </div>
      </LabeledRow>
    );
  }
  if (change.key === "model") {
    return (
      <InlineRow
        label={change.label}
        edit
        from={<ModelSelectCompact value={change.before} display={undefined} />}
      >
        <ModelSelectCompact value={change.after} display={undefined} />
      </InlineRow>
    );
  }
  return (
    <InlineRow label={change.label} edit from={valueText(change.before)}>
      {valueText(change.after)}
    </InlineRow>
  );
}

function Frame({
  view,
  children,
}: {
  view: ApprovalView;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 pt-[6px] text-xs">
      {view.op && <OpBadge op={view.op} className="mt-0.5" />}
      <div className="min-w-0 flex-1">
        {view.entity && view.op && (
          <div className="flex min-w-0 items-center gap-2">
            <HeadEntity entity={view.entity} op={view.op} />
          </div>
        )}
        <FieldList className="mt-1.5">{children}</FieldList>
      </div>
    </div>
  );
}

function Changes({
  changes,
  error,
}: {
  changes: AgentChange[] | undefined;
  error: unknown;
}) {
  if (error) {
    return (
      <NoxusAlert
        variant="destructive"
        size="xs"
        description={errorDetail(error)}
      />
    );
  }
  if (!changes) {
    return <p className="text-muted-foreground">Reading the change…</p>;
  }
  if (changes.length === 0) {
    return <p className="text-muted-foreground">Nothing changes.</p>;
  }
  return (
    <>
      {changes.map((change) => (
        <ChangeRow key={change.key} change={change} />
      ))}
    </>
  );
}

// A decided call is never previewed again: the agent has moved on since, so a
// preview would describe today's agent, not the change the user decided on.
function decidedNote(part: ToolPartBase): string {
  if (getApprovalDecision(part)?.approved === false) {
    return "Rejected, so nothing changed.";
  }
  // Approved but refused when it ran (a patch the server would not take).
  if (part.state === "output-error" || part.state === "error") {
    return part.errorText
      ? `Failed, so nothing changed: ${part.errorText}`
      : "Failed, so nothing changed.";
  }
  return "This change ran; what it changed was not kept.";
}

// ``agents_update``: each setting it changes, before and after.
export function AgentChangesBody({
  view,
  part,
}: {
  view: ApprovalView;
  part: ToolPartBase;
}) {
  const { spotGroup } = useSpotUser();
  const input = isRecord(part.input) ? part.input : {};
  const ran = ranChanges(part);
  const awaiting = isActionablePendingApproval(part);
  const agentId = view.entity?.id ?? "";
  const preview = useRemoteData(
    DATA_KEYS.ASSISTANT_CHANGES,
    {
      path: { group_id: spotGroup.id, assistant_id: agentId },
      body: {
        name: typeof input.name === "string" ? input.name : null,
        description:
          typeof input.description === "string" ? input.description : null,
        image_url: typeof input.icon === "string" ? input.icon : null,
        patch: isRecord(input.patch) ? input.patch : null,
        preview: input.preview === true,
      },
    },
    { enabled: awaiting && !!spotGroup.id && !!agentId, retry: false }
  );
  if (!ran && !awaiting) {
    return (
      <Frame view={view}>
        <p className="text-muted-foreground">{decidedNote(part)}</p>
      </Frame>
    );
  }
  return (
    <Frame view={view}>
      <Changes
        changes={ran ?? (preview.data?.changes as AgentChange[] | undefined)}
        error={ran ? null : preview.error}
      />
    </Frame>
  );
}

// ``agents_create``: the new agent's fields, then each setting its patch gives
// it, so creating an agent with its tables or knowledge bases is one card.
export function NewAgentBody({
  view,
  part,
}: {
  view: ApprovalView;
  part: ToolPartBase;
}) {
  const { spotGroup } = useSpotUser();
  const input = isRecord(part.input) ? part.input : {};
  const patch = isRecord(input.patch) ? input.patch : null;
  const ran = ranChanges(part);
  const awaiting = isActionablePendingApproval(part);
  const preview = useRemoteData(
    DATA_KEYS.NEW_ASSISTANT_CHANGES,
    { path: { group_id: spotGroup.id }, body: { patch } },
    { enabled: awaiting && !!spotGroup.id && !!patch, retry: false }
  );
  return (
    <EntityApprovalBody view={view} input={part.input}>
      {patch && (ran || awaiting) && (
        <Changes
          changes={ran ?? (preview.data?.changes as AgentChange[] | undefined)}
          error={ran ? null : preview.error}
        />
      )}
    </EntityApprovalBody>
  );
}

function ranMoves(part: ToolPartBase): CapabilityMove[] | null {
  const output = part.state === "output-available" ? part.output : undefined;
  const rows = isRecord(output) ? output.result : output;
  if (!Array.isArray(rows)) return null;
  return rows.flatMap((row) =>
    isRecord(row) &&
    typeof row.name === "string" &&
    typeof row.title === "string" &&
    typeof row.previous_version === "number" &&
    typeof row.current_version === "number" &&
    row.previous_version !== row.current_version
      ? [
          {
            name: row.name,
            title: row.title,
            from: row.previous_version,
            to: row.current_version,
          },
        ]
      : []
  );
}

// ``agents_pin_capabilities``: each capability's version, from and to.
export function CapabilityPinsBody({
  view,
  part,
}: {
  view: ApprovalView;
  part: ToolPartBase;
}) {
  const { spotGroup } = useSpotUser();
  const input = isRecord(part.input) ? part.input : {};
  const pins = isRecord(input.pins) ? input.pins : {};
  const ran = ranMoves(part);
  const awaiting = isActionablePendingApproval(part);
  const agentId = view.entity?.id ?? "";
  // The server's plan, which names every capability that moves: a version
  // can pull another along with it.
  const preview = useRemoteData(
    DATA_KEYS.ASSISTANT_CHANGES,
    {
      path: { group_id: spotGroup.id, assistant_id: agentId },
      body: { pins: pins as Record<string, number> },
    },
    { enabled: awaiting && !!spotGroup.id && !!agentId, retry: false }
  );
  if (!ran && !awaiting) {
    return (
      <Frame view={view}>
        <p className="text-muted-foreground">{decidedNote(part)}</p>
      </Frame>
    );
  }
  const moves =
    ran ??
    preview.data?.capabilities?.map((move) => ({
      name: move.name,
      title: move.title,
      from: move.from_version,
      to: move.to_version,
    })) ??
    null;
  return (
    <Frame view={view}>
      {preview.error && !ran ? (
        <NoxusAlert
          variant="destructive"
          size="xs"
          title={errorDetail(preview.error)}
        />
      ) : !moves ? (
        <p className="text-muted-foreground">Reading the change…</p>
      ) : moves.length === 0 ? (
        <p className="text-muted-foreground">
          The agent already runs these versions, so nothing changes.
        </p>
      ) : (
        moves.map((move) => (
          <InlineRow
            key={move.name}
            label={move.title}
            edit
            from={versionText(move.from)}
          >
            {versionText(move.to)}
          </InlineRow>
        ))
      )}
      {moves && moves.length > 0 && (
        <p className="text-muted-foreground">
          A backup version of the agent is saved first.
        </p>
      )}
    </Frame>
  );
}
